import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, CreditCard, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export default function PayBalance() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${bookingId}/public`).then((r) => setBooking(r.data)).catch(() => setError("Booking not found."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const pay = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/payments/balance-checkout", { booking_id: bookingId, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  const paid = booking?.balance_paid;
  const nothingOwing = booking && Number(booking.final_balance) <= 0;

  return (
    <div className="min-h-screen bg-[#0b0f17] text-slate-200">
      <Navbar />
      <section className="mx-auto max-w-xl px-4 py-16" data-testid="pay-balance-page">
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">Detail complete</div>
        <h1 className="font-display mt-2 text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-slate-100">Pay your balance</h1>
        <div className="cj-card p-6 mt-8">
          {!booking && !error && <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading booking…</div>}
          {booking && (
            <>
              <div className="font-display uppercase font-extrabold text-2xl text-slate-100">{booking.service_name}</div>
              <div className="font-mono text-xs text-slate-500 mt-1">{booking.scheduled_date} · {booking.customer.address}</div>
              <div className="my-5 h-px bg-white/5" />
              <div className="flex items-center justify-between">
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">Balance due now</div>
                <div className="font-display font-extrabold text-2xl text-[#ff6b00]">${Number(booking.final_balance).toFixed(2)}</div>
              </div>
              {paid ? (
                <div className="mt-6 text-emerald-300 text-sm" data-testid="balance-already-paid">Balance paid in full — thank you!</div>
              ) : nothingOwing ? (
                <div className="mt-6 text-slate-300 text-sm" data-testid="balance-nothing-owing">There's no balance owing on this booking.</div>
              ) : (
                <button onClick={pay} disabled={busy} data-testid="pay-balance-button" className="cj-btn-primary mt-6 w-full px-6 py-3 rounded-full inline-flex items-center justify-center gap-2">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Pay ${Number(booking.final_balance).toFixed(2)} balance <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </>
          )}
          {error && <div className="mt-4 text-sm text-red-400 font-mono uppercase tracking-wider" data-testid="pay-balance-error">{error}</div>}
        </div>
        <Link to="/" className="mt-6 inline-block text-sm text-slate-400 hover:text-slate-200">← Back home</Link>
      </section>
      <Footer />
    </div>
  );
}
